import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import ReCAPTCHA from 'react-google-recaptcha';
import { contactService } from '../services/contact/contactService';
import { UilCheckCircle, UilUser, UilPhone, UilEnvelope, UilClipboardNotes } from '@iconscout/react-unicons';
import CleanHome from '../assets/Images/CleanHome.jpg';

const FEATURES = [
  { title: 'Highly Rated',           description: '5.0 stars on Google & Yelp' },
  { title: 'Family Owned Since 1998', description: '25+ years of experience' },
  { title: 'Free Custom Quotes',     description: 'No hidden fees or obligations' },
  { title: 'Licensed & Insured',     description: 'Fully bonded for your peace of mind' },
  { title: '100% Satisfaction',      description: 'We guarantee your happiness' },
];

const BENEFITS = [
  'Same-day quotes available',
  'Flexible scheduling options',
  'Professional & trained staff',
  'Customizable cleaning plans',
];

export default function Request() {
  const [data, setData] = useState({ name: '', phone: '', email: '', projectDescription: '' });
  const [errors, setErrors] = useState({});
  const [agreed, setAgreed] = useState(false);
  const [recaptcha, setRecaptcha] = useState('');
  const [status, setStatus] = useState('idle');

  const validate = () => {
    const errs = {};
    if (!data.name)               errs.name               = 'Please enter your name';
    if (!data.phone)              errs.phone              = 'Please enter your phone';
    if (!data.email)              errs.email              = 'Please enter your email';
    if (!data.projectDescription) errs.projectDescription = 'Please describe your project';
    if (!agreed)                  errs.agreed             = 'You must agree to terms';
    if (!recaptcha)               errs.recaptcha          = 'Complete the reCAPTCHA';
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleChange = (e) => {
    setData((d) => ({ ...d, [e.target.name]: e.target.value }));
    setErrors((er) => ({ ...er, [e.target.name]: '' }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setStatus('sending');
    const result = await contactService.sendContactRequest({ ...data, recaptcha });
    if (result.success) {
      setStatus('success');
      setData({ name: '', phone: '', email: '', projectDescription: '' });
      setAgreed(false); setRecaptcha('');
    } else {
      setStatus('error');
    }
  };

  const inputCls = (field) =>
    `w-full pl-10 pr-4 py-3 border rounded-xl text-sm transition-colors focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent ${
      errors[field] ? 'border-red-400 bg-red-50' : 'border-slate-200 hover:border-slate-300'
    }`;

  return (
    <div className="bg-white">
      <Helmet>
        <title>Request Free Estimate - Fabiana Fabulous Cleaning Services LLC</title>
        <meta name="description" content="Get a free cleaning quote today. No-obligation estimates for home, office, and Airbnb cleaning in Seattle & Eastside. Fast and transparent pricing." />
        <meta name="keywords" content="free cleaning estimate, get cleaning quote Seattle, request cleaning service, cleaning price quote" />
        <link rel="canonical" href="https://fabycleaners.com/request" />
      </Helmet>

      {/* Hero */}
      <section className="relative min-h-[55vh] flex items-end bg-cover bg-center" style={{ backgroundImage: `url(${CleanHome})` }}>
        <div className="absolute inset-0 bg-gradient-to-t from-slate-900 via-slate-900/60 to-slate-900/30" />
        <div className="relative z-10 w-full max-w-7xl mx-auto px-6 py-16">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}>
            <span className="inline-block bg-amber-400 text-amber-900 text-xs font-bold px-3 py-1.5 rounded-full mb-4 uppercase tracking-wide">
              Special Offer: 15% OFF First Cleaning
            </span>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-4 leading-tight">
              Get Your <span className="text-gold-400">Free</span> Estimate
            </h1>
            <p className="text-lg text-white/70 max-w-xl">No-obligation quotes tailored to your home or business.</p>
            <div className="flex gap-8 mt-8">
              {[['25+', 'Years'], ['5.0', 'Rating'], ['5000+', 'Clients']].map(([v, l]) => (
                <div key={l} className="text-center">
                  <p className="text-3xl font-bold text-gold-400">{v}</p>
                  <p className="text-xs text-white/50 uppercase tracking-wider mt-0.5">{l}</p>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-16 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">

            {/* Left – Why us */}
            <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.7, delay: 0.2 }} className="space-y-6">
              <div className="bg-white rounded-2xl border border-slate-100 shadow-card p-8">
                <h2 className="text-xl font-bold text-slate-900 mb-6">Why Choose Us</h2>
                <div className="space-y-4">
                  {FEATURES.map((f) => (
                    <div key={f.title} className="flex items-start gap-3 py-3 border-b border-slate-50 last:border-0">
                      <div className="w-8 h-8 bg-brand-50 rounded-lg flex items-center justify-center shrink-0 mt-0.5">
                        <UilCheckCircle size={16} className="text-brand-600" />
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-slate-900">{f.title}</p>
                        <p className="text-xs text-slate-500 mt-0.5">{f.description}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-brand-600 rounded-2xl p-8 text-white">
                <h3 className="text-lg font-bold mb-5">What You Get</h3>
                <ul className="space-y-3">
                  {BENEFITS.map((b) => (
                    <li key={b} className="flex items-center gap-3 text-sm">
                      <div className="w-5 h-5 bg-brand-500 rounded-full flex items-center justify-center shrink-0">
                        <UilCheckCircle size={12} className="text-white" />
                      </div>
                      <span className="text-brand-50">{b}</span>
                    </li>
                  ))}
                </ul>
                <div className="mt-6 pt-5 border-t border-brand-500">
                  <p className="text-sm text-brand-100">Need help? Call <a href="tel:4258296092" className="text-white font-semibold hover:underline">(425) 829-6092</a></p>
                </div>
              </div>
            </motion.div>

            {/* Right – Form */}
            <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.7, delay: 0.2 }}>
              <div className="bg-white rounded-2xl border border-slate-100 shadow-card p-8 sticky top-24">
                {status === 'success' ? (
                  <motion.div initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} className="text-center py-12">
                    <div className="w-16 h-16 bg-brand-50 rounded-full flex items-center justify-center mx-auto mb-4">
                      <UilCheckCircle size={32} className="text-brand-600" />
                    </div>
                    <h2 className="text-2xl font-bold text-slate-900 mb-2">Request Sent!</h2>
                    <p className="text-slate-500 mb-8">We'll contact you shortly via SMS with your personalized quote.</p>
                    <button onClick={() => setStatus('idle')}
                      className="border-2 border-brand-600 text-brand-700 hover:bg-brand-50 font-semibold px-6 py-2.5 rounded-xl transition-colors text-sm">
                      Send Another Request
                    </button>
                  </motion.div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-5">
                    <div>
                      <h2 className="text-xl font-bold text-slate-900">Request Your Quote</h2>
                      <p className="text-sm text-slate-500 mt-1">We'll respond within 24 hours</p>
                    </div>

                    {status === 'error' && (
                      <div className="bg-red-50 border border-red-200 rounded-xl px-4 py-3">
                        <p className="text-sm text-red-600">Submission failed. Please try again or call us directly.</p>
                      </div>
                    )}

                    {[
                      { label: 'Full Name',     name: 'name',  type: 'text',  placeholder: 'John Doe',            Icon: UilUser },
                      { label: 'Phone Number',  name: 'phone', type: 'tel',   placeholder: '(425) 123-4567',      Icon: UilPhone },
                      { label: 'Email Address', name: 'email', type: 'email', placeholder: 'john@example.com',    Icon: UilEnvelope },
                    ].map(({ label, name, type, placeholder, Icon }) => (
                      <div key={name}>
                        <label className="block text-sm font-semibold text-slate-700 mb-1.5">{label}</label>
                        <div className="relative">
                          <Icon className="absolute top-3.5 left-3 text-slate-400" size={16} />
                          <input name={name} type={type} placeholder={placeholder}
                            value={data[name]} onChange={handleChange} className={inputCls(name)} />
                        </div>
                        {errors[name] && <p className="text-xs text-red-500 mt-1">{errors[name]}</p>}
                      </div>
                    ))}

                    <div>
                      <label className="block text-sm font-semibold text-slate-700 mb-1.5">Project Description</label>
                      <div className="relative">
                        <UilClipboardNotes className="absolute top-3 left-3 text-slate-400" size={16} />
                        <textarea name="projectDescription" placeholder="Tell us about your cleaning needs"
                          rows={3} value={data.projectDescription} onChange={handleChange}
                          className={`${inputCls('projectDescription')} resize-none`} />
                      </div>
                      {errors.projectDescription && <p className="text-xs text-red-500 mt-1">{errors.projectDescription}</p>}
                    </div>

                    <div className="flex justify-center">
                      <ReCAPTCHA sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY}
                        onChange={(v) => { setRecaptcha(v); setErrors((er) => ({ ...er, recaptcha: '' })); }} />
                    </div>
                    {errors.recaptcha && <p className="text-xs text-red-500 text-center">{errors.recaptcha}</p>}

                    <label className="flex items-start gap-2.5 cursor-pointer">
                      <input type="checkbox" checked={agreed}
                        onChange={() => { setAgreed(!agreed); setErrors((er) => ({ ...er, agreed: '' })); }}
                        className="mt-0.5 rounded border-slate-300 text-brand-600 focus:ring-brand-500" />
                      <span className="text-xs text-slate-600">
                        I agree to the{' '}
                        <Link to="/privacy" className="text-brand-600 hover:underline">Privacy Policy</Link>{' '}and{' '}
                        <Link to="/terms-and-conditions" className="text-brand-600 hover:underline">Terms & Conditions</Link>
                      </span>
                    </label>
                    {errors.agreed && <p className="text-xs text-red-500">{errors.agreed}</p>}

                    <button type="submit" disabled={status === 'sending'}
                      className="w-full bg-brand-600 hover:bg-brand-700 disabled:opacity-50 text-white font-semibold py-3.5 rounded-xl transition-colors text-sm">
                      {status === 'sending'
                        ? <span className="flex items-center justify-center gap-2"><span className="fresh-spinner w-4 h-4 border-2 border-brand-300 border-t-white inline-block" />Sending…</span>
                        : 'Get Your Free Quote'}
                    </button>
                    <p className="text-xs text-slate-400 text-center">Typically responds within 2 hours during business hours</p>
                  </form>
                )}
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
}
